import React, { useState, useEffect } from "react";
import {
  Card,
  Form,
  Input,
  Select,
  Button,
  Row,
  Col,
  Divider,
  Typography,
  message,
  Spin,
  Alert,
  Space,
  Tag,
  Tooltip,
} from "antd";
import {
  ArrowLeftOutlined,
  DownloadOutlined,
  ExperimentOutlined,
  UserOutlined,
  HomeOutlined,
  ThunderboltOutlined,
  NumberOutlined,
  CheckCircleOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import dayjs from "dayjs";

// Import vendor signature and seal
import vendorSeal from "../assets/seal_nms.jpeg";
import vendorSign from "../assets/vendor_sign.png";
import api from "../services/api";

const { Title, Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;

const DEBUG_METER = false;

const MeterTestingForm = () => {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const formValues = Form.useWatch([], form);

  const [customers, setCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerOrders, setCustomerOrders] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);

  const [loading, setLoading] = useState(false);
  const [loadingCustomers, setLoadingCustomers] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [searchCustomer, setSearchCustomer] = useState("");

  const dbg = (...args) => {
    if (!DEBUG_METER) return;
    console.log("[METER TESTING DEBUG]", ...args);
  };

  // Fetch customers list - EXACT same pattern as your WCR
  const fetchCustomers = async () => {
    try {
      setLoadingCustomers(true);
      dbg("Fetching customers from /api/customers?limit=1000");
      
      const data = await api.get("/customers?limit=1000");
      dbg("Response data:", data);
      
      // Check different possible response formats
      if (data.success && Array.isArray(data.data)) {
        setCustomers(data.data);
        dbg("customers loaded from data.data:", data.data.length);
        message.success(`Loaded ${data.data.length} customers`);
      } else {
        dbg("fetchCustomers unexpected format:", data);
        setCustomers([]);
        message.warning("No customers found or unexpected response format");
      }
    } catch (error) {
      dbg("fetchCustomers network error:", error);
      message.error("Failed to fetch customers: " + error.message);
      setCustomers([]);
    } finally {
      setLoadingCustomers(false);
    }
  };

  useEffect(() => {
    fetchCustomers();
  }, []);

  // Filter customers based on search
  const filteredCustomers = customers.filter((customer) => {
    if (!searchCustomer) return true;
    const search = searchCustomer.toLowerCase();
    return (
      (customer.customerName?.toLowerCase().includes(search)) ||
      (customer.consumerNo?.toLowerCase().includes(search)) ||
      (customer.customerMobile?.includes(search)) ||
      (customer.customerEmail?.toLowerCase().includes(search)) ||
      (customer.name?.toLowerCase().includes(search)) || // Some APIs use 'name' instead
      (String(customer.id)?.includes(search))
    );
  });

  dbg("Total customers:", customers.length);
  dbg("Filtered customers:", filteredCustomers.length);
  dbg("First customer sample:", customers[0]);

  // Fetch customer details with orders - same pattern as WCR
  const fetchCustomerDetails = async (customerId) => {
    try {
      dbg(`Fetching customer details for ID: ${customerId}`);
      const json = await api.get(`/customers/${customerId}`);
      dbg("Customer details response:", json);
      
      if (!json.success || !json.data) {
        dbg("fetchCustomerDetails unexpected json:", json);
        return null;
      }

      const c = json.data;

      // Normalize orders - handle different formats
      const ordersRaw = c.orders || c.order || [];
      const normalizedOrders = (Array.isArray(ordersRaw) ? ordersRaw : [ordersRaw]).filter(Boolean).map((o) => ({
        ...o,
        items: o.items || o.orderitem || [],
      }));

      // Normalize assets
      const normalizedAssets = c.assets || c.serializedasset || [];

      const normalizedCustomer = {
        ...c,
        orders: normalizedOrders,
        assets: normalizedAssets,
      };

      dbg("customer normalized:", {
        id: normalizedCustomer.id,
        name: normalizedCustomer.customerName || normalizedCustomer.name,
        ordersCount: normalizedCustomer.orders?.length,
      });

      return normalizedCustomer;
    } catch (error) {
      dbg("fetchCustomerDetails network error:", error);
      return null;
    }
  };

  // Extract meter serial number from assets
  const extractMeterSerialNumber = (order, customerAssets = []) => {
    // Try to find generation meter from assets
    const orderAssets = (customerAssets || []).filter(
      (asset) => asset.orderId === order.id || asset.customerId === order.customerId
    );

    dbg("Looking for meter in assets:", orderAssets.length);

    // Look for meter assets
    const meterAssets = orderAssets.filter((asset) => {
      const itemName = asset.item?.itemName?.toLowerCase() || "";
      const brand = asset.brand?.toLowerCase() || "";
      const assetType = asset.assetType?.toLowerCase() || "";
      return (
        itemName.includes("meter") ||
        brand.includes("meter") ||
        assetType.includes("meter") ||
        itemName.includes("gen") ||
        itemName.includes("generation") ||
        asset.serialNumber
      );
    });

    if (meterAssets.length > 0) {
      const meterAsset = meterAssets[0];
      dbg("Found meter asset:", meterAsset);
      return meterAsset.serialNumber || meterAsset.batchId || meterAsset.assetNumber || "";
    }

    // Try to get from order meta
    if (order.metadata && order.metadata.meterSerialNumber) {
      return order.metadata.meterSerialNumber;
    }

    return "";
  };

  // Field status indicator component
  const FieldStatus = ({ fieldName }) => {
    const value = form.getFieldValue(fieldName);

    if (!value || value.toString().trim() === "") {
      return (
        <Tooltip title="Please fill this field">
          <WarningOutlined style={{ color: "#faad14", marginLeft: 8, fontSize: "12px" }} />
        </Tooltip>
      );
    }

    return (
      <Tooltip title="Field has data">
        <CheckCircleOutlined style={{ color: "#52c41a", marginLeft: 8, fontSize: "12px" }} />
      </Tooltip>
    );
  };

  // Handle customer selection
  const handleCustomerChange = async (customerId) => {
    setLoading(true);
    try {
      customerId = Number(customerId);
      dbg("Selected customerId:", customerId);

      const customer = customers.find((c) => Number(c.id) === customerId);
      if (!customer) {
        dbg("Customer not found in list for id:", customerId);
        message.error("Customer not found");
        return;
      }

      dbg("Found customer:", customer);

      // Fetch detailed customer with orders
      const detailedCustomer = await fetchCustomerDetails(customerId);
      const finalCustomer = detailedCustomer || customer;

      setSelectedCustomer(finalCustomer);

      const orders = finalCustomer.orders || [];
      setCustomerOrders(orders);

      dbg("Customer orders:", orders.length);

      // Choose latest order
      let latestOrder = null;
      if (orders.length > 0) {
        const ordersSorted = [...orders].sort((a, b) => {
          const dateA = a.createdAt ? new Date(a.createdAt) : new Date(0);
          const dateB = b.createdAt ? new Date(b.createdAt) : new Date(0);
          return dateB - dateA;
        });
        latestOrder = ordersSorted[0];
      }

      setSelectedOrder(latestOrder);

      // Auto-populate form fields (except meterSrNo - user input only)
      // Get customer name - handle different field names
      const customerName = finalCustomer.customerName || finalCustomer.name || "";
      
      // Get pin code - handle different field names
      const pinCode = finalCustomer.pincode || finalCustomer.pinCode || finalCustomer.postalCode || "";

      const fullAddress = finalCustomer.customerAddress || finalCustomer.address || "";

      // Get consumer number - prefer order's selectedConsumerNumber
      const consumerNo = latestOrder?.selectedConsumerNumber || finalCustomer.consumerNo || finalCustomer.consumerNumber || finalCustomer.customerNumber || "";

      const nextValues = {
        consumerName: customerName,
        address: fullAddress,
        pinCode: pinCode,
        consumerNo: consumerNo,
        solarPlantCapacity: latestOrder?.plantSizeKw?.toString() || latestOrder?.capacity?.toString() || "3",
        sanctionLoad: latestOrder?.plantSizeKw?.toString() || latestOrder?.capacity?.toString() || "3",
        // meterSrNo is NOT auto-populated - user must enter manually
        date: dayjs(),
      };

      dbg("Form nextValues:", nextValues);
      form.setFieldsValue(nextValues);

      message.success("Customer details loaded successfully");
    } catch (error) {
      dbg("handleCustomerChange error:", error);
      message.error("Error loading customer details: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  // Handle order selection change
  const handleOrderChange = (orderId) => {
    const order = customerOrders.find((o) => o.id === orderId);
    if (!order) return;

    setSelectedOrder(order);

    // Update order-related fields (meterSrNo is NOT auto-populated - user input only)
    const updates = {
      consumerNo: order.selectedConsumerNumber || selectedCustomer?.consumerNo || form.getFieldValue("consumerNo") || "",
      solarPlantCapacity: order.plantSizeKw?.toString() || order.capacity?.toString() || "3",
      sanctionLoad: order.plantSizeKw?.toString() || order.capacity?.toString() || "3",
      // meterSrNo is NOT updated - user must enter manually
    };

    dbg("Order change updates:", updates);
    form.setFieldsValue(updates);
    message.info(`Switched to Order #${order.id} - ${order.plantSizeKw || order.capacity || 0}KW`);
  };

  // Generate PDF exactly as per DOC file with exact colors
  // Generate PDF exactly as per DOC file with exact colors
const generatePDF = async (values) => {
  setGenerating(true);
  
  try {
    const formattedDate = values.date 
      ? dayjs(values.date).format("DD/MM/YYYY") 
      : dayjs().format("DD/MM/YYYY");

    dbg("Generating PDF with values:", values);

    // Create a container for PDF content
    const pdfContainer = document.createElement("div");
    pdfContainer.style.width = "800px";
    pdfContainer.style.padding = "40px";
    pdfContainer.style.fontFamily = "Arial, sans-serif";
    pdfContainer.style.background = "white";
    pdfContainer.style.position = "absolute";
    pdfContainer.style.left = "-9999px";
    
    // Build the PDF content exactly as per the DOC file - WITH EXACT COLORS
    pdfContainer.innerHTML = `
      <div style="max-width: 100%; margin: 0 auto;">
        <!-- Company Name and Address exactly as per DOC file with exact colors -->
        <div style="margin-bottom: 20px; text-align: center;">
          <p style="font-size: 20px; font-weight: bold; margin: 0; letter-spacing: 0.5px; color: #58cf68;">M/S. NIYATI MULTI SERVICES</p>
          <p style="font-size: 14px; margin: 5px 0 2px 0; color: #333333; font-weight: 600;">(REGISTERED VENDOR AT NATIONAL PORTAL)</p>
          <p style="font-size: 13px; margin: 5px 0; color: #333333; line-height: 1.4;">A/P- Shop No. 02, Ashok Engineers, Pune - Nashik Highway, Narayangaon, Tal- Junnar, Dist- Pune</p>
          <p style="font-size: 13px; margin: 5px 0; color: #555555;">Mob: +8830089973  |  GSTN: 27BXAPA2680L1ZI  |  Email: sales.niyatimultiservices@gmail.com</p>
        </div>
        
        <!-- Date on right side -->
        <div style="text-align: right; margin-bottom: 20px;">
          <span style="font-size: 14px; font-weight: normal; color: #000000;">Date: ${formattedDate}</span>
        </div>
        
        <div style="margin-bottom: 30px;">
          <p style="font-weight: bold; margin: 0; color: #000000;">To,</p>
          <p style="font-weight: bold; margin: 5px 0; color: #000000;">Testing Division</p>
          ${(values.divisionAddress || '').split('\n').map(line => `<p style="margin: 5px 0; color: #000000;">${line}</p>`).join('')}
        </div>
        
        <div style="margin-bottom: 30px;">
          <p style="font-weight: bold; font-size: 14px; color: #000000;">Subject: Requesting for solar Generation meter testing</p>
        </div>
        
        <div style="margin-bottom: 20px;">
          <p style="color: #000000; margin: 0 0 10px 0;">Dear Sir/Madam,</p>
          <p style="color: #000000; margin: 0;">With the reference of above subject, please test the net meter & generation meter purchase by consumer for solar PV Plant. The details are as below.</p>
        </div>
        
        <div style="margin-bottom: 30px; padding: 15px 0;">
          <p style="margin: 10px 0; color: #000000;"><span style="font-weight: bold;">Consumer Name:</span> ${values.consumerName || ''}</p>
          <p style="margin: 10px 0; color: #000000;"><span style="font-weight: bold;">Address:</span> ${values.address || ''}</p>
          <p style="margin: 10px 0; color: #000000;"><span style="font-weight: bold;">Pin Code:</span> ${values.pinCode || ''}</p>
          <p style="margin: 10px 0; color: #000000;"><span style="font-weight: bold;">Consumer No. :</span> ${values.consumerNo || ''}</p>
          <p style="margin: 10px 0; color: #000000;"><span style="font-weight: bold;">Solar Plant Capacity :</span> ${values.solarPlantCapacity || ''} Kw</p>
          <p style="margin: 10px 0; color: #000000;"><span style="font-weight: bold;">Sanction Load :</span> ${values.sanctionLoad || ''} kw</p>
        </div>
        
        <div style="margin-bottom: 30px;">
          <p style="font-weight: bold; margin-bottom: 5px; font-size: 14px; color: #000000;">Generation Meter :</p>
          <p style="font-weight: bold; margin-top: 0; font-size: 14px; color: #000000;">Sr. No :</p>
        </div>
        
        <!-- Signature and Stamp on RIGHT side -->
        <div style="margin-top: 60px; display: flex; justify-content: flex-end; align-items: center; gap: 20px;">
          <div style="display: flex; flex-direction: column; align-items: center;">
            <img src="${vendorSign}" alt="Signature" style="width: 120px; height: auto; margin-bottom: 5px;" />
            <img src="${vendorSeal}" alt="Seal" style="width: 100px; height: auto;" />
          </div>
          <div>
            <p style="font-weight: bold; margin: 0; font-size: 14px; color: #000000;">Vishal Hande</p>
            <p style="margin: 5px 0; font-size: 14px; color: #000000;">+918830089973</p>
            <p style="margin: 5px 0; font-style: italic; font-size: 14px; color: #000000;">Authorized Signature.</p>
          </div>
        </div>
      </div>
    `;
    
    document.body.appendChild(pdfContainer);
    
    // Capture as canvas with better quality
    const canvas = await html2canvas(pdfContainer, {
      scale: 2,
      backgroundColor: "#ffffff",
      logging: false,
      allowTaint: true,
      useCORS: true,
    });
    
    document.body.removeChild(pdfContainer);
    
    // Create PDF
    const imgData = canvas.toDataURL("image/png");
    
    // Calculate dimensions - A4 portrait
    const pdf = new jsPDF({
      orientation: "portrait",
      unit: "mm",
      format: "a4",
    });
    
    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = (canvas.height * pdfWidth) / canvas.width;
    
    pdf.addImage(
      imgData,
      "PNG",
      0,
      0,
      pdfWidth,
      pdfHeight,
      undefined,
      "FAST"
    );
    
    // Save PDF
    const fileName = `Meter_Testing_${values.consumerNo || 'Customer'}_${dayjs().format("DDMMYYYY")}.pdf`;
    pdf.save(fileName);
    
    message.success(`PDF generated successfully: ${fileName}`);
    
  } catch (error) {
    dbg("PDF generation error:", error);
    message.error("Failed to generate PDF: " + error.message);
  } finally {
    setGenerating(false);
  }
};
  // Handle form submission
  const onFinish = (values) => {
    if (!selectedCustomer) {
      message.warning("Please select a customer first");
      return;
    }
    generatePDF(values);
  };

  return (
    <div style={{ padding: "0 0 24px 0" }}>
      {DEBUG_METER && (
        <Alert
          type="info"
          showIcon
          title="Meter Testing Debug Mode ON"
          description={
            <div>
              <p>Open browser console to see logs. Set DEBUG_METER=false to disable.</p>
              <p style={{ marginTop: 8, fontWeight: "bold" }}>
                Customers loaded: {customers.length} | Filtered: {filteredCustomers.length}
              </p>
            </div>
          }
          style={{ marginBottom: 16 }}
        />
      )}

      <div style={{ marginBottom: 24, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <Button
              icon={<ArrowLeftOutlined />}
              onClick={() => navigate("/msedcl")}
              size="large"
            >
              Back
            </Button>
            <Title level={2} style={{ margin: 0, color: "#722ed1" }}>
              <ExperimentOutlined style={{ marginRight: 12 }} />
              Meter Testing Letter
            </Title>
          </div>
          <Text type="secondary" style={{ marginLeft: 60 }}>
            Generate solar generation meter testing request letter for MSEDCL
          </Text>
        </div>
        <Button 
          type="default" 
          icon={<DownloadOutlined />} 
          onClick={fetchCustomers}
          loading={loadingCustomers}
        >
          Refresh Customers
        </Button>
      </div>

      <Spin spinning={loading || generating}>
        <Form form={form} layout="vertical" onFinish={onFinish}>
          <Row gutter={24}>
            <Col xs={24} lg={12}>
              <Card
                title={
                  <Space>
                    <div style={{ background: "#f9f0ff", padding: "6px", borderRadius: "8px", display: "flex" }}>
                      <UserOutlined style={{ color: "#722ed1" }} />
                    </div>
                    <span style={{ color: "#722ed1", fontWeight: "bold" }}>Step 1: Select Customer</span>
                  </Space>
                }
                bordered={false}
                style={{ borderRadius: 16, boxShadow: "0 4px 12px rgba(0,0,0,0.05)", marginBottom: 24 }}
              >
                <Row gutter={24}>
                  <Col span={24}>
                    <Form.Item
                      label="Search Customer"
                      name="customerId"
                      rules={[{ required: true, message: "Please select a customer" }]}
                      extra={
                        <span>
                          Search by name, consumer number, or mobile... 
                          <Text type="danger" style={{ marginLeft: 8 }}>
                            {filteredCustomers.length === 0 && customers.length > 0 ? 
                              "No matches found" : 
                              customers.length === 0 ? 
                              "Loading customers..." : 
                              `${filteredCustomers.length} customers available`
                            }
                          </Text>
                        </span>
                      }
                    >
                      <Select
                        showSearch
                        placeholder="Type to search..."
                        onChange={handleCustomerChange}
                        onSearch={setSearchCustomer}
                        filterOption={false}
                        loading={loadingCustomers}
                        size="large"
                        style={{ borderRadius: 8 }}
                        notFoundContent={loadingCustomers ? "Loading..." : "No customers found"}
                      >
                        {filteredCustomers.map((customer) => (
                          <Option key={customer.id} value={customer.id}>
                            <div style={{ display: "flex", flexDirection: "column", padding: "4px 0" }}>
                              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                <Text strong>{customer.customerName || customer.name || "Unnamed"}</Text>
                                {customer.consumerNo && <Tag color="purple">Consumer</Tag>}
                              </div>
                              <Text type="secondary" style={{ fontSize: 12 }}>
                                {customer.consumerNo && `No: ${customer.consumerNo} • `}
                                {customer.customerMobile || customer.mobile || ""}
                                {customer.id && ` • ID: ${customer.id}`}
                              </Text>
                            </div>
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                    
                    {customers.length === 0 && !loadingCustomers && (
                      <Alert
                        message="No customers found"
                        description="Try refreshing or check API connection"
                        type="warning"
                        showIcon
                        action={
                          <Button size="small" onClick={fetchCustomers}>
                            Refresh
                          </Button>
                        }
                      />
                    )}
                  </Col>
                </Row>

                {selectedCustomer && (
                  <div
                    style={{
                      marginTop: 16,
                      padding: 20,
                      borderRadius: 12,
                      border: "1px solid #f0f0f0",
                      backgroundColor: "#fafafa",
                    }}
                  >
                    <Row gutter={24}>
                      <Col xs={24} md={12}>
                        <Title level={5} style={{ marginTop: 0, color: "#722ed1" }}>
                          Customer Summary
                        </Title>
                        <Space direction="vertical" style={{ width: "100%" }}>
                          <div style={{ display: "flex", justifyContent: "space-between" }}>
                            <Text type="secondary">Name:</Text> 
                            <Text strong>{selectedCustomer.customerName || selectedCustomer.name}</Text>
                          </div>
                          <div style={{ display: "flex", justifyContent: "space-between" }}>
                            <Text type="secondary">Consumer No:</Text> 
                            <Text>{selectedCustomer.consumerNo || selectedCustomer.consumerNumber || "N/A"}</Text>
                          </div>
                          <div style={{ display: "flex", justifyContent: "space-between" }}>
                            <Text type="secondary">Contact:</Text> 
                            <Text>{selectedCustomer.customerMobile || selectedCustomer.mobile || "N/A"}</Text>
                          </div>
                          <div style={{ display: "flex", justifyContent: "space-between" }}>
                            <Text type="secondary">Orders:</Text> 
                            <Text>{customerOrders.length}</Text>
                          </div>
                        </Space>
                      </Col>
                      <Col xs={24} md={12}>
                        <Title level={5} style={{ marginTop: 0, color: "#722ed1" }}>
                          Select Order
                        </Title>
                        {customerOrders.length > 0 ? (
                          <Form.Item label="Associated Order" style={{ marginBottom: 0 }}>
                            <Select
                              placeholder="Choose Order"
                              value={selectedOrder?.id}
                              onChange={handleOrderChange}
                              style={{ width: "100%" }}
                            >
                              {customerOrders.map((order) => (
                                <Option key={order.id} value={order.id}>
                                  Order #{order.id} - {order.plantSizeKw || order.capacity || 0}KW 
                                  {order.projectStatus ? ` (${order.projectStatus})` : ""}
                                </Option>
                              ))}
                            </Select>
                          </Form.Item>
                        ) : (
                          <Alert 
                            message="No orders found" 
                            description="This customer has no associated orders" 
                            type="warning" 
                            showIcon 
                          />
                        )}
                      </Col>
                    </Row>
                  </div>
                )}
              </Card>

              {selectedCustomer && (
                <Card
                  title={
                    <Space>
                      <div style={{ background: "#f9f0ff", padding: "6px", borderRadius: "8px", display: "flex" }}>
                        <HomeOutlined style={{ color: "#722ed1" }} />
                      </div>
                      <span style={{ color: "#722ed1", fontWeight: "bold" }}>Step 2: Consumer & Meter Details</span>
                    </Space>
                  }
                  bordered={false}
                  style={{ borderRadius: 16, boxShadow: "0 4px 12px rgba(0,0,0,0.05)", marginBottom: 24 }}
                >
                  <Row gutter={[16, 16]}>
                    <Col xs={24} md={12}>
                      <Form.Item
                        label="Testing Division Address"
                        name="divisionAddress"
                        rules={[{ required: true, message: "Division address is required" }]}
                      >
                        <TextArea rows={2} placeholder={"Line 1\nLine 2"} />
                      </Form.Item>
                    </Col>

                    <Col xs={24}>
                      <Form.Item
                        label={
                          <Space>
                            Consumer Name <FieldStatus fieldName="consumerName" />
                          </Space>
                        }
                        name="consumerName"
                        rules={[{ required: true, message: "Consumer name is required" }]}
                      >
                        <Input
                          prefix={<UserOutlined style={{ color: "#722ed1" }} />}
                          placeholder="Auto-filled from selection"
                          readOnly
                          style={{ backgroundColor: "#fafafa" }}
                        />
                      </Form.Item>
                    </Col>

                    <Col xs={24}>
                      <Form.Item
                        label={
                          <Space>
                            Address <FieldStatus fieldName="address" />
                          </Space>
                        }
                        name="address"
                        rules={[{ required: true, message: "Address is required" }]}
                      >
                        <TextArea 
                          rows={2} 
                          placeholder="Auto-filled from selection" 
                          readOnly 
                          style={{ backgroundColor: "#fafafa" }}
                        />
                      </Form.Item>
                    </Col>

                    <Col xs={24} md={8}>
                      <Form.Item
                        label={
                          <Space>
                            Pin Code <FieldStatus fieldName="pinCode" />
                          </Space>
                        }
                        name="pinCode"
                        rules={[{ required: true, message: "Pin code is required" }]}
                      >
                        <Input placeholder="Auto-filled" readOnly style={{ backgroundColor: "#fafafa" }} />
                      </Form.Item>
                    </Col>

                    <Col xs={24} md={16}>
                      <Form.Item
                        label={
                          <Space>
                            Consumer Number <FieldStatus fieldName="consumerNo" />
                          </Space>
                        }
                        name="consumerNo"
                        rules={[{ required: true, message: "Consumer number is required" }]}
                      >
                        <Input 
                          prefix={<NumberOutlined style={{ color: "#722ed1" }} />} 
                          placeholder="Auto-filled" 
                          readOnly 
                          style={{ backgroundColor: "#fafafa" }}
                        />
                      </Form.Item>
                    </Col>

                    <Divider style={{ margin: "24px 0 16px", borderColor: "#722ed140" }}>
                      <Text style={{ color: "#722ed1", fontWeight: "bold" }}>Solar Plant Details</Text>
                    </Divider>

                    <Col xs={24} md={12}>
                      <Form.Item
                        label={
                          <Space>
                            Solar Plant Capacity <FieldStatus fieldName="solarPlantCapacity" />
                          </Space>
                        }
                        name="solarPlantCapacity"
                        rules={[{ required: true, message: "Capacity is required" }]}
                      >
                        <Input 
                          suffix="kW" 
                          placeholder="Auto-filled" 
                          readOnly 
                          style={{ backgroundColor: "#fafafa" }}
                        />
                      </Form.Item>
                    </Col>

                    <Col xs={24} md={12}>
                      <Form.Item
                        label={
                          <Space>
                            Sanction Load <FieldStatus fieldName="sanctionLoad" />
                          </Space>
                        }
                        name="sanctionLoad"
                        rules={[{ required: true, message: "Sanction load is required" }]}
                      >
                        <Input 
                          suffix="kW" 
                          placeholder="Auto-filled" 
                          readOnly 
                          style={{ backgroundColor: "#fafafa" }}
                        />
                      </Form.Item>
                    </Col>

                    <Divider style={{ margin: "24px 0 16px", borderColor: "#722ed140" }}>
                      <Text style={{ color: "#722ed1", fontWeight: "bold" }}>Meter Information</Text>
                    </Divider>


                    <Col xs={24}>
                      <Form.Item
                        label={
                          <Space>
                            Letter Date <FieldStatus fieldName="date" />
                          </Space>
                        }
                        name="date"
                        rules={[{ required: true, message: "Date is required" }]}
                      >
                        <Input 
                          value={dayjs().format("DD/MM/YYYY")} 
                          readOnly 
                          style={{ backgroundColor: "#fafafa" }}
                        />
                      </Form.Item>
                    </Col>
                  </Row>

                  <Divider />

                  <Form.Item style={{ marginBottom: 0 }}>
                    <Button
                      type="primary"
                      htmlType="submit"
                      size="large"
                      icon={<DownloadOutlined />}
                      loading={generating}
                      style={{
                        backgroundColor: "#722ed1",
                        borderColor: "#722ed1",
                        width: "100%",
                        height: "48px",
                        fontSize: "16px",
                        borderRadius: "8px",
                      }}
                    >
                      Generate Meter Testing Letter PDF
                    </Button>
                  </Form.Item>
                </Card>
              )}
            </Col>

            <Col xs={24} lg={12}>
              <Card
                title={
                  <Space>
                    <ExperimentOutlined style={{ color: "#722ed1" }} />
                    <span style={{ color: "#722ed1", fontWeight: "bold" }}>Document Preview & Instructions</span>
                  </Space>
                }
                bordered={false}
                style={{
                  borderRadius: 16,
                  boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
                  position: "sticky",
                  top: 24,
                  border: "2px solid #722ed140",
                  backgroundColor: "#fafafa",
                }}
              >
                <Space direction="vertical" size="large" style={{ width: "100%" }}>
                  {selectedCustomer ? (
                    <Alert
                      message="Customer Selected"
                      description={
                        <div>
                          <Text strong>{selectedCustomer.customerName || selectedCustomer.name}</Text>
                          <br />
                          <Text type="secondary">{selectedCustomer.consumerNo || selectedCustomer.consumerNumber}</Text>
                        </div>
                      }
                      type="success"
                      showIcon
                      icon={<CheckCircleOutlined />}
                    />
                  ) : (
                    <Alert
                      message="No Customer Selected"
                      description="Please select a customer to generate the meter testing letter"
                      type="info"
                      showIcon
                    />
                  )}

                  <Divider style={{ margin: "12px 0" }} />

                  <div>
                    <Text strong style={{ fontSize: "16px", color: "#722ed1" }}>
                      📄 Document Format (MSEDCL Standard):
                    </Text>
                    <ul style={{ marginTop: "12px", paddingLeft: "20px", lineHeight: "1.8" }}>
                      <li>✓ <span style={{ fontWeight: "bold", color: "#1a4587" }}>M/S. NIYATI MULTI SERVICES</span></li>
                      <li>✓ <span style={{ color: "#e31e24", fontWeight: "600" }}>(REGISTER VENDOR AT NATIONAL PORTAL)</span></li>
                      <li>✓ <span style={{ color: "#333333" }}>A/P- Shop No. 02, Ashok Engineers, Pune - Nashik Highway, Narayangaon, Tal- Junnar, Dist- Pune</span></li>
                      <li>✓ <span style={{ color: "#555555" }}>Mob: +8830089973 | GSTN: 27BXAPA2680L1ZI | Email: sales.niyatimultiservices@gmail.com</span></li>
                      <li>✓ Date displayed on top-right corner</li>
                      <li>✓ Recipient: Testing Division, {formValues?.divisionAddress ? formValues.divisionAddress.replace('\n', ', ') : "Narayangaon, Pune"}</li>
                      <li>✓ Subject: Requesting for solar Generation meter testing</li>
                      <li>✓ All consumer details in standard format</li>
                      <li>✓ Generation meter serial number field</li>
                      <li>✓ Authorized signature and company seal at bottom</li>
                    </ul>
                  </div>

                  <Divider style={{ margin: "12px 0" }} />

                  <div>
                    <Text strong style={{ fontSize: "16px", color: "#722ed1" }}>
                      👤 Authorized Signatory:
                    </Text>
                    <div style={{ 
                      marginTop: "12px", 
                      padding: "16px", 
                      background: "#fff", 
                      borderRadius: "8px",
                      border: "1px solid #d9d9d9",
                      display: "flex",
                      alignItems: "center",
                      gap: "20px"
                    }}>
                      <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                        <img src={vendorSign} alt="Signature" style={{ width: "100px", height: "auto", marginBottom: "5px" }} />
                        <img src={vendorSeal} alt="Seal" style={{ width: "80px", height: "auto" }} />
                      </div>
                      <div>
                        <Text strong style={{ fontSize: "16px" }}>Vishal Hande</Text>
                        <br />
                        <Text type="secondary">+918830089973</Text>
                        <br />
                        <Text type="secondary" style={{ fontStyle: "italic" }}>Authorized Signature</Text>
                      </div>
                    </div>
                  </div>

                  {selectedOrder && (
                    <>
                      <Divider style={{ margin: "12px 0" }} />
                      <div>
                        <Text strong style={{ color: "#722ed1" }}>Selected Order Details:</Text>
                        <div style={{ marginTop: "8px", padding: "12px", background: "#fff", borderRadius: "8px" }}>
                          <Text>Order ID: #{selectedOrder.id}</Text>
                          <br />
                          <Text>Plant Capacity: {selectedOrder.plantSizeKw || selectedOrder.capacity || 0} kW</Text>
                          <br />
                          <Text>Sanction Number: {selectedOrder.sanctionNumber || "N/A"}</Text>
                        </div>
                      </div>
                    </>
                  )}
                </Space>
              </Card>
            </Col>
          </Row>
        </Form>
      </Spin>
    </div>
  );
};

export default MeterTestingForm;
